<template>
  <ModalComponent
    ref="modalRef"
    submitButtonText="Create Invoice"
    :submit="handleSubmit"
    :submitDisabled="
      isSubmitting || !isFormValid || !solderaBankAccount?.bank_account
    "
    size="lg"
    @close="handleClose"
  >
    <template #title>Create Sales Invoice</template>
    <template #body>
      <form @submit.prevent="handleSubmit">
        <!-- Invoice Header Info -->
        <div class="card border mb-3">
          <div class="card-body">
            <div class="row">
              <div class="col-6">
                <small class="text-muted">From:</small>
                <p class="mb-0 fw-bold">Soldera OÜ</p>
                <p class="mb-0 small">Akadeemia tn 3, 51003 Tartu</p>
                <p class="mb-0 small">Reg: 16785003</p>
                <p class="mb-0 small">VAT: EE102643242</p>
              </div>
              <div class="col-6">
                <div data-test="soldera-bank-account">
                  <template v-if="solderaBankAccount?.bank_account">
                    <template v-if="solderaBankAccount.bank_account.iban">
                      <small class="text-muted">Bank Account (IBAN):</small>
                      <p class="mb-0 small font-monospace">
                        {{ solderaBankAccount.bank_account.iban }}
                      </p>
                    </template>
                    <template
                      v-for="detail in solderaBankAccountDetails"
                      :key="detail.label"
                    >
                      <small class="text-muted d-block mt-2"
                        >{{ detail.label }}:</small
                      >
                      <p class="mb-0 small font-monospace">
                        {{ detail.value }}
                      </p>
                    </template>
                  </template>
                  <p
                    v-else-if="solderaBankAccount"
                    class="mb-0 small text-danger"
                  >
                    No Soldera bank account in
                    {{ solderaBankAccount.currency }}; the invoice will be
                    refused.
                  </p>
                  <div
                    v-else-if="solderaBankAccountLoadFailed"
                    class="small text-danger"
                  >
                    <p class="mb-1">
                      Could not load Soldera's {{ currency }} bank account.
                    </p>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      data-test="soldera-bank-account-retry"
                      @click="loadSolderaBankAccount"
                    >
                      Retry
                    </button>
                  </div>
                </div>
                <small class="text-muted d-block mt-2">Invoice Date:</small>
                <p class="mb-0 small">{{ invoiceDate }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-3">
          <label for="description" class="form-label">Description *</label>
          <textarea
            id="description"
            v-model="formData.description"
            class="form-control"
            rows="3"
            placeholder="e.g., GO Purchase - Wind Energy 2024"
            required
          ></textarea>
          <div class="form-text">
            Description of the Guarantees of Origin being sold
          </div>
        </div>

        <div class="mb-3">
          <label for="amount" class="form-label">Amount (MWh) *</label>
          <input
            id="amount"
            type="number"
            v-model.number="formData.amount"
            class="form-control"
            min="1"
            step="1"
            required
          />
          <div class="form-text">Quantity of GOs in MWh</div>
        </div>

        <div class="mb-3">
          <label for="unitPrice" class="form-label"
            >Unit Price ({{ unitPriceUnit }}) *</label
          >
          <input
            id="unitPrice"
            type="number"
            v-model.number="formData.unitPrice"
            class="form-control"
            min="0.0001"
            step="0.0001"
            required
          />
          <div class="form-text">Price per MWh before VAT</div>
        </div>

        <div class="mb-3">
          <label for="reference" class="form-label">Reference (Optional)</label>
          <input
            id="reference"
            type="text"
            v-model="formData.reference"
            class="form-control"
            :maxlength="INVOICE_REFERENCE_MAX_LENGTH"
            placeholder="e.g., Contract-2024-001"
          />
          <div class="form-text">Optional reference number for the invoice</div>
        </div>

        <!-- Additional fee items -->
        <hr class="my-4" />
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h6 class="mb-0">Additional Fees</h6>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            data-test="add-fee-item"
            @click="addFeeItem"
          >
            <FontAwesomeIcon icon="plus" class="me-1" />Add fee
          </button>
        </div>
        <div class="form-text mb-3">
          Extra fee lines on the same invoice. Amount (MWh) follows the main
          line.
        </div>
        <div v-if="feeItems.length > 0" class="mb-3">
          <div class="row g-2 small text-muted">
            <div class="col-md-3">Fee type</div>
            <div class="col-md-4">Description</div>
            <div class="col-md-2" data-test="fee-unit-price-header">
              Unit price ({{ unitPriceUnit }})
            </div>
            <div class="col-md-2">Amount (MWh)</div>
            <div class="col-md-1"></div>
          </div>
          <div
            v-for="(feeItem, index) in feeItems"
            :key="index"
            class="row g-2 mt-0 align-items-start"
          >
            <div class="col-md-3">
              <select
                v-model="feeItem.itemType"
                class="form-select"
                data-test="fee-item-type"
              >
                <option
                  v-for="option in FEE_ITEM_TYPE_OPTIONS"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <input
                type="text"
                v-model="feeItem.description"
                class="form-control"
                :class="{ 'is-invalid': feeItemErrors[index].description }"
                placeholder="Description"
                data-test="fee-description"
              />
              <div class="invalid-feedback" data-test="fee-description-error">
                {{ feeItemErrors[index].description }}
              </div>
            </div>
            <div class="col-md-2">
              <input
                type="number"
                v-model.number="feeItem.unitPrice"
                class="form-control"
                :class="{ 'is-invalid': feeItemErrors[index].unitPrice }"
                min="0.0001"
                step="0.0001"
                :placeholder="unitPriceUnit"
                data-test="fee-unit-price"
              />
              <div class="invalid-feedback" data-test="fee-unit-price-error">
                {{ feeItemErrors[index].unitPrice }}
              </div>
            </div>
            <div class="col-md-2">
              <input
                type="number"
                :value="formData.amount"
                class="form-control"
                disabled
              />
            </div>
            <div class="col-md-1">
              <button
                type="button"
                class="btn btn-outline-danger"
                data-test="remove-fee-item"
                @click="removeFeeItem(index)"
              >
                <FontAwesomeIcon icon="trash" />
              </button>
            </div>
          </div>
        </div>

        <!-- Buyer Company Details -->
        <hr class="my-4" />
        <h6 class="mb-3">Buyer Company Details</h6>

        <div class="mb-3">
          <label for="buyerName" class="form-label">Company Name *</label>
          <input
            id="buyerName"
            type="text"
            v-model="formData.buyerName"
            class="form-control"
            :maxlength="BUYER_NAME_MAX_LENGTH"
            required
          />
        </div>

        <div class="mb-3">
          <label for="buyerAddress" class="form-label">Address *</label>
          <textarea
            id="buyerAddress"
            v-model="formData.buyerAddress"
            class="form-control"
            rows="2"
            :maxlength="BUYER_ADDRESS_MAX_LENGTH"
            required
          ></textarea>
        </div>

        <div class="mb-3">
          <label for="buyerCountry" class="form-label"
            >Country (for VAT calculation) *</label
          >
          <SearchableSelectComponent
            :options="ALL_COUNTRIES"
            v-model="formData.buyerCountry"
            placeholder="Select country..."
            :autoOpen="true"
          />
          <div class="form-text">
            Country used for VAT calculation. Auto-filled from the VAT number
            when one is entered.
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="buyerRegistryCode" class="form-label"
              >Registry Code</label
            >
            <input
              id="buyerRegistryCode"
              type="text"
              v-model="formData.buyerRegistryCode"
              class="form-control"
              :maxlength="BUYER_REGISTRY_CODE_MAX_LENGTH"
            />
          </div>

          <div class="col-md-6 mb-3">
            <label for="buyerVat" class="form-label">VAT Number</label>
            <input
              id="buyerVat"
              type="text"
              v-model="formData.buyerVat"
              class="form-control"
              :maxlength="BUYER_VAT_MAX_LENGTH"
            />
          </div>
        </div>

        <div class="mb-3">
          <label for="buyerEmails" class="form-label">
            Buyer Email Addresses (Optional)
          </label>
          <textarea
            id="buyerEmails"
            v-model="formData.buyerEmails"
            class="form-control"
            rows="2"
            placeholder="accountant@buyer.com, finance@buyer.com"
          ></textarea>
          <div class="form-text">
            Enter email addresses separated by commas. Invoice can be sent after
            creation.
          </div>
        </div>

        <hr class="my-4" />

        <div class="alert alert-info" role="alert">
          <strong>Total:</strong>
          {{ formattedTotalAmount }} (excl. VAT)
        </div>
      </form>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import {
  currencyLabel,
  formatMoney,
  formatMoneyWithCurrency,
} from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { getCountryCodeFromVat } from "@/composables/useValidators";
import { ALL_COUNTRIES } from "@/constants";
import { DEFAULT_CURRENCY, type Currency } from "@/money/currency_constants";
import { SOLDERA_COMPANY_ID } from "@/money/money_constants";
import {
  invoiceApi,
  type BuyerSalesInvoiceCreateRequest,
  type SolderaBankAccountResponse,
} from "@/money/invoice/invoiceApi";
import {
  BUYER_ADDRESS_MAX_LENGTH,
  BUYER_NAME_MAX_LENGTH,
  BUYER_REGISTRY_CODE_MAX_LENGTH,
  BUYER_VAT_MAX_LENGTH,
  INVOICE_REFERENCE_MAX_LENGTH,
} from "@/money/invoice/invoiceLimits";
import { sumBy } from "lodash-es";
import { computed, nextTick, reactive, ref, useTemplateRef, watch } from "vue";

const props = withDefaults(
  defineProps<{
    show: boolean;
    companyId: number;
    /** The deal currency: the invoice's prices and the Soldera account it names are in it. */
    currency: Currency;
    // Every buyer invoice belongs to a deal; null only while no deal is selected and the modal is closed.
    salesTransactionId: number | null;
    preFillDescription?: string;
    preFillAmount?: number;
    preFillUnitPrice?: number | null;
    preFillBuyerName?: string;
    preFillBuyerAddress?: string;
    preFillBuyerCountry?: string;
    preFillBuyerRegistryCode?: string;
    preFillBuyerVat?: string;
    preFillBuyerEmails?: string;
  }>(),
  {
    preFillDescription: "",
    preFillAmount: 1,
    preFillUnitPrice: 0,
    preFillBuyerName: "",
    preFillBuyerAddress: "",
    preFillBuyerCountry: "",
    preFillBuyerRegistryCode: "",
    preFillBuyerVat: "",
    preFillBuyerEmails: "",
  }
);

const emit = defineEmits<{
  close: [];
  "invoice-created": [invoiceId?: number];
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const isSubmitting = ref(false);
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const isModalOpen = ref(false);
// null until the account for this opening has loaded.
const solderaBankAccount = ref<SolderaBankAccountResponse | null>(null);
const solderaBankAccountLoadFailed = ref(false);
// Bumped by every account load and every close, so a response from an earlier opening is dropped.
let solderaBankAccountLoadId = 0;
const invoiceDate = computed(() => {
  return new Date().toLocaleDateString("en-GB");
});

const formData = reactive({
  description: "",
  amount: 1,
  unitPrice: 0.0,
  reference: "",
  buyerName: "",
  buyerAddress: "",
  buyerCountry: "",
  buyerRegistryCode: "",
  buyerVat: "",
  buyerEmails: "",
});

interface FeeItemForm {
  itemType: string;
  description: string;
  unitPrice: number;
}

const FEE_ITEM_TYPE_OPTIONS = [
  { value: "FEE_SOLDERA_PROCUREMENT", label: "Soldera procurement fee" },
];

const feeItems = ref<FeeItemForm[]>([]);

const addFeeItem = () => {
  feeItems.value.push({
    itemType: "FEE_SOLDERA_PROCUREMENT",
    description: "Soldera procurement fee",
    unitPrice: 0.0,
  });
};

const removeFeeItem = (index: number) => {
  feeItems.value.splice(index, 1);
};

const unitPriceUnit = computed(() => `${currencyLabel(props.currency)}/MWh`);

// After the IBAN: a UK or US domestic route, then the Swift/BIC everyone abroad pays by. An EUR
// deal's buyer pays by SEPA, which needs the IBAN alone.
const solderaBankAccountDetails = computed(() => {
  const account = solderaBankAccount.value?.bank_account;
  if (!account) return [];
  const details = [
    { label: "Account Number", value: account.account_number },
    { label: "Sort Code", value: account.sort_code },
    { label: "Routing Number", value: account.routing_number },
    { label: "Account Type", value: account.account_type },
    {
      label: "Swift/BIC",
      value: props.currency === DEFAULT_CURRENCY ? null : account.swift_bic,
    },
  ];
  return details.filter((detail) => detail.value !== null);
});

const totalAmount = computed(() => {
  const feeUnitPriceSum = sumBy(feeItems.value, (item) => item.unitPrice || 0);
  return formData.amount * (formData.unitPrice + feeUnitPriceSum);
});

// An EUR total keeps the bare figure it always showed; other currencies name theirs.
const formattedTotalAmount = computed(() =>
  props.currency === DEFAULT_CURRENCY
    ? formatMoney(totalAmount.value)
    : formatMoneyWithCurrency(totalAmount.value, props.currency)
);

interface FeeItemErrors {
  description: string;
  unitPrice: string;
}

// Row-level messages so an admin sees which fee field is blocking the submit.
const feeItemErrors = computed<FeeItemErrors[]>(() =>
  feeItems.value.map((item) => ({
    description:
      item.description.trim().length > 0 ? "" : "Description is required",
    unitPrice: item.unitPrice > 0 ? "" : "Unit price must be greater than 0",
  }))
);

const areFeeItemsValid = computed(() =>
  feeItemErrors.value.every(
    (errors) => !errors.description && !errors.unitPrice
  )
);

// maxlength only limits typing; prefilled or pasted values are checked here so the backend cap is never hit.
const isWithinLimits = computed(
  () =>
    formData.reference.trim().length <= INVOICE_REFERENCE_MAX_LENGTH &&
    formData.buyerName.trim().length <= BUYER_NAME_MAX_LENGTH &&
    formData.buyerAddress.trim().length <= BUYER_ADDRESS_MAX_LENGTH &&
    formData.buyerRegistryCode.trim().length <=
      BUYER_REGISTRY_CODE_MAX_LENGTH &&
    formData.buyerVat.trim().length <= BUYER_VAT_MAX_LENGTH
);

const isFormValid = computed(() => {
  return (
    formData.description.trim().length > 0 &&
    formData.amount > 0 &&
    formData.unitPrice > 0 &&
    formData.buyerName.trim().length > 0 &&
    formData.buyerAddress.trim().length > 0 &&
    formData.buyerCountry.length > 0 &&
    isWithinLimits.value &&
    areFeeItemsValid.value
  );
});

// Populate buyer data when modal opens
const populateFormData = () => {
  formData.description = props.preFillDescription || "";
  formData.amount = props.preFillAmount;
  formData.unitPrice = props.preFillUnitPrice || 0.0;
  formData.buyerName = props.preFillBuyerName;
  formData.buyerAddress = props.preFillBuyerAddress || "";
  formData.buyerCountry = props.preFillBuyerCountry || "";
  formData.buyerRegistryCode = props.preFillBuyerRegistryCode || "";
  formData.buyerVat = props.preFillBuyerVat || "";
  formData.buyerEmails = props.preFillBuyerEmails || "";
  feeItems.value = [];
};

// Watch for modal show to populate buyer data and toggle modal
watch(
  () => props.show,
  async (shouldShow) => {
    await nextTick();
    if (modalRef.value && isModalOpen.value !== shouldShow) {
      if (shouldShow) {
        populateFormData();
        loadSolderaBankAccount();
      } else {
        resetSolderaBankAccount();
      }
      modalRef.value.toggleModal();
      isModalOpen.value = shouldShow;
    }
  }
);

// Resolve the VAT calculation country from the buyer's VAT number whenever it changes.
// The registry counterparty country (used to pre-fill the field) is often different from
// where the buyer is actually registered, so a present VAT number is the better source.
// This only fires on VAT changes, so a manual country override afterward is preserved.
watch(
  () => formData.buyerVat,
  (vat) => {
    const resolved = getCountryCodeFromVat(vat);
    if (resolved) {
      formData.buyerCountry = resolved;
    }
  }
);

const resetSolderaBankAccount = () => {
  solderaBankAccountLoadId += 1;
  solderaBankAccount.value = null;
  solderaBankAccountLoadFailed.value = false;
};

const loadSolderaBankAccount = async () => {
  resetSolderaBankAccount();
  const loadId = solderaBankAccountLoadId;
  const isCurrent = () => loadId === solderaBankAccountLoadId;
  try {
    const account = await invoiceApi.getSolderaBankAccount(props.currency);
    if (isCurrent() && account.currency === props.currency) {
      solderaBankAccount.value = account;
    }
  } catch (error) {
    if (isCurrent()) {
      solderaBankAccountLoadFailed.value = true;
      globalErrorHandler(error);
    }
  }
};

const handleSubmit = async () => {
  if (
    !isFormValid.value ||
    !solderaBankAccount.value?.bank_account ||
    isSubmitting.value
  ) {
    return;
  }

  isSubmitting.value = true;

  try {
    const payload: BuyerSalesInvoiceCreateRequest = {
      company_id: props.companyId || SOLDERA_COMPANY_ID, // For external buyers, we use Soldera company ID
      sales_transaction_id: props.salesTransactionId,
      description: formData.description.trim(),
      amount: formData.amount,
      unit_price: formData.unitPrice,
      reference: formData.reference.trim() || null,
      buyer_name: formData.buyerName.trim(),
      buyer_address: formData.buyerAddress.trim(),
      buyer_country: formData.buyerCountry,
      buyer_registry_code: formData.buyerRegistryCode.trim() || null,
      buyer_vat: formData.buyerVat.trim() || null,
      buyer_emails: formData.buyerEmails.trim() || null,
      fee_items: feeItems.value.map((item) => ({
        item_type: item.itemType,
        description: item.description.trim(),
        unit_price: item.unitPrice,
      })),
    };

    const created = await invoiceApi.createBuyerSalesInvoice(payload);

    globalSuccessHandler("Success", "Sales invoice created successfully");
    emit("invoice-created", created?.invoice?.id);
    closeModal();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  // Reset form
  formData.description = "";
  formData.amount = 1;
  formData.unitPrice = 0.0;
  formData.reference = "";
  formData.buyerName = "";
  formData.buyerAddress = "";
  formData.buyerCountry = "";
  formData.buyerRegistryCode = "";
  formData.buyerVat = "";
  formData.buyerEmails = "";
  feeItems.value = [];
  resetSolderaBankAccount();
  isModalOpen.value = false;
  emit("close");
};

// Dismissing mid-submit is ignored; a successful submit closes via closeModal directly.
const handleClose = () => {
  if (!isSubmitting.value) {
    closeModal();
  }
};
</script>

<style scoped lang="scss"></style>
